// M3 占位 — v1 用 mock 数据，M8 接真实 API
// 今日概览：4 KPI + 趋势 + 抓取状态 + 顶部文章
// 对齐 WeChat MP 「数据分析」:顶 Segmented 切今日/近 7/30/90 天,4 KPI 卡去 shadow 用 1px border
import { useState } from 'react'
import { Card, Segmented } from 'antd'
import { Link } from 'react-router-dom'
import { ExternalLink } from 'lucide-react'
import PageHeader from '@/components/PageHeader'
import KPICard from '@/components/KPICard'
import TrendChart from '@/components/TrendChart'
import ScrapeStatus from '@/components/ScrapeStatus'
import { useOverview, useScrapeStatus, useFanTrend, useReadTrend } from '@/mocks/dashboard'
import { formatNumber, timeAgo } from '@/lib/utils'

type Range = 'today' | '7d' | '30d' | '90d'

const RANGE_LABEL: Record<Range, string> = {
  today: '今日',
  '7d': '7 日',
  '30d': '30 日',
  '90d': '90 日',
}

export default function Dashboard() {
  const [range, setRange] = useState<Range>('today')
  const overview = useOverview()
  const scrape = useScrapeStatus()
  const fan7d = useFanTrend(7)
  const fan30d = useFanTrend(30)
  const fan90d = useFanTrend(90)
  const read90d = useReadTrend(90)

  const o = overview.data
  const s = scrape.data

  // 微信风:日 KPI 优先取今日,7/30/90 天切到累计
  const fanTrend =
    range === '7d' ? fan7d.data :
    range === '30d' ? fan30d.data :
    range === '90d' ? fan90d.data :
    (o?.fanTrend7d ?? [])
  const readTrend =
    range === '7d' ? (o?.readTrend30d ?? []) :
    range === '90d' ? (read90d.data ?? []) :
    (o?.readTrend30d ?? [])
  const topArticles = o?.topArticles ?? []

  return (
    <div>
      <PageHeader
        title="今日概览"
        subtitle="公众号数据资产与分析总览"
        extra={
          <div className="flex items-center gap-3">
            <Segmented
              value={range}
              onChange={(v) => setRange(v as Range)}
              options={[
                { label: '今日', value: 'today' },
                { label: '近 7 天', value: '7d' },
                { label: '近 30 天', value: '30d' },
                { label: '近 90 天', value: '90d' },
              ]}
              className="shadow-sm"
            />
            <Link
              to="/articles"
              className="text-sm text-primary-500 hover:text-primary-600 flex items-center gap-1 transition-colors"
            >
              查看文章 <ExternalLink size={14} />
            </Link>
          </div>
        }
      />

      {/* 4 KPI 卡 — 1px border + 弱化阴影 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPICard
          label={range === 'today' ? '今日阅读' : '累计阅读'}
          value={o?.kpi.weeklyRead ?? null}
          delta={o?.kpi.weeklyReadDelta}
          deltaLabel="%"
          loading={overview.isLoading}
          className="card-interactive"
        />
        <KPICard
          label={range === 'today' ? '今日留言' : `${RANGE_LABEL[range]}留言`}
          value={o?.kpi.weeklyComment ?? null}
          delta={o?.kpi.weeklyCommentDelta}
          deltaLabel="%"
          loading={overview.isLoading}
          className="card-interactive"
        />
        <KPICard
          label="累计粉丝"
          value={o?.kpi.cumulateUser ?? null}
          delta={o?.kpi.cumulateDelta}
          deltaLabel="本周净增"
          unit="人"
          loading={overview.isLoading}
          className="card-interactive"
        />
        <KPICard
          label="互动率"
          value={o?.kpi.weeklyRead && o?.kpi.weeklyComment
            ? (o.kpi.weeklyComment / Math.max(o.kpi.weeklyRead, 1)) * 100
            : null}
          delta={o?.kpi.healthDelta}
          deltaLabel="vs 上周"
          unit="%"
          loading={overview.isLoading}
          className="card-interactive"
        />
      </div>

      {/* 趋势 + 抓取状态 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card title="粉丝增长趋势" className="lg:col-span-2 card-interactive" size="small">
          <TrendChart data={fanTrend} height={260} area />
        </Card>
        <Card title="抓取状态" size="small" className="card-interactive">
          {s ? (
            <div className="flex flex-col gap-3 py-2">
              <ScrapeStatus status={s} />
              {typeof s.dataGapDays !== 'undefined' && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-ink-500">数据断档</span>
                  <span className="text-ink-900 num font-medium">{s.dataGapDays} 天</span>
                </div>
              )}
              {typeof s.queueTasks !== 'undefined' && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-ink-500">队列任务</span>
                  <span className="text-ink-900 num font-medium">{s.queueTasks}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-ink-500">最后抓取</span>
                <span className="text-ink-900 text-xs">{timeAgo(s.lastRunAt)}</span>
              </div>
            </div>
          ) : (
            <div className="text-sm text-ink-300 py-8 text-center">加载中…</div>
          )}
        </Card>
      </div>

      {/* 30 日阅读 + Top 文章 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title={`阅读数趋势 (${range === '90d' ? '90d' : '30d'})`} className="lg:col-span-2 card-interactive" size="small">
          <TrendChart data={readTrend} height={240} area />
        </Card>
        <Card title="今日 Top 5 文章" size="small" className="card-interactive">
          <ul className="flex flex-col">
            {topArticles.slice(0, 5).map((a, i) => (
              <li
                key={a.id}
                className="flex items-center gap-2 text-sm py-2 border-b border-ink-100 last:border-0 group hover:bg-ink-50 transition-colors rounded"
              >
                <span
                  className={`num text-xs w-5 text-center shrink-0 ${
                    i < 3 ? 'text-primary-600 font-semibold' : 'text-ink-400'
                  }`}
                >
                  {i + 1}
                </span>
                <Link
                  to={`/articles/${a.id}`}
                  className="flex-1 truncate text-ink-700 hover:text-primary-600 no-underline transition-colors"
                  title={a.title}
                >
                  {a.title}
                </Link>
                <span className="num text-ink-500 text-xs flex-shrink-0 group-hover:text-primary-600 transition-colors">
                  {formatNumber(a.read)}
                </span>
              </li>
            ))}
            {topArticles.length === 0 && (
              <li className="text-sm text-ink-300 py-6 text-center">暂无数据</li>
            )}
          </ul>
        </Card>
      </div>

      {/* Task 18: 4 个新数据 card (interface / menu / message / comments) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {/* 接口分析 — Top N 接口调用次数 */}
        <Card title="接口调用 Top 5" size="small" className="card-interactive">
          <ul className="flex flex-col">
            {(o?.interfaces ?? []).slice(0, 5).map((iface, i) => (
              <li
                key={iface.interfaceName}
                className="flex items-center gap-2 text-sm py-2 border-b border-ink-100 last:border-0 group hover:bg-ink-50 transition-colors"
              >
                <span
                  className={`num text-xs w-5 text-center shrink-0 ${
                    i < 3 ? 'text-primary-600 font-semibold' : 'text-ink-400'
                  }`}
                >
                  {i + 1}
                </span>
                <span
                  className="flex-1 truncate text-ink-700 font-mono text-xs"
                  title={iface.interfaceName}
                >
                  {iface.interfaceName}
                </span>
                <span className="num text-ink-500 text-xs flex-shrink-0 group-hover:text-primary-600 transition-colors">
                  {formatNumber(iface.callCount)}
                </span>
              </li>
            ))}
            {(o?.interfaces ?? []).length === 0 && (
              <li className="text-sm text-ink-300 py-6 text-center">暂无数据</li>
            )}
          </ul>
        </Card>

        {/* 菜单点击 — Top 菜单 */}
        <Card title="菜单点击 Top 5" size="small" className="card-interactive">
          <ul className="flex flex-col">
            {(o?.menus ?? []).slice(0, 5).map((m, i) => (
              <li
                key={`${m.menuId}-${i}`}
                className="flex items-center gap-2 text-sm py-2 border-b border-ink-100 last:border-0 group hover:bg-ink-50 transition-colors"
              >
                <span
                  className={`num text-xs w-5 text-center shrink-0 ${
                    i < 3 ? 'text-primary-600 font-semibold' : 'text-ink-400'
                  }`}
                >
                  {i + 1}
                </span>
                <span className="flex-1 truncate text-ink-700" title={m.menuName}>
                  {m.menuName}
                </span>
                <span className="num text-ink-500 text-xs flex-shrink-0 group-hover:text-primary-600 transition-colors">
                  {formatNumber(m.clickCount)}
                </span>
              </li>
            ))}
            {(o?.menus ?? []).length === 0 && (
              <li className="text-sm text-ink-300 py-6 text-center">暂无数据</li>
            )}
          </ul>
        </Card>

        {/* 消息分析 — 总数 + 类型分布 */}
        <Card title="消息数（近 7 天）" size="small" className="card-interactive">
          <div className="flex flex-col gap-2 py-2">
            <div className="flex items-baseline gap-1.5">
              <span className="kpi-value num">
                {o?.messages ? formatNumber(o.messages.total) : '—'}
              </span>
              <span className="text-sm text-ink-500">条</span>
            </div>
            {o?.messages.byType && o.messages.byType.length > 0 ? (
              <ul className="flex flex-col gap-1 mt-1">
                {o.messages.byType.slice(0, 4).map((t) => (
                  <li
                    key={t.msgType}
                    className="flex items-center justify-between text-xs hover:bg-ink-50 px-2 py-1 rounded transition-colors"
                  >
                    <span className="text-ink-500">{t.msgType}</span>
                    <span className="num text-ink-700">{formatNumber(t.count)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-ink-300 py-4 text-center">暂无数据</div>
            )}
          </div>
        </Card>

        {/* 留言 — 总数 + 精选数 */}
        <Card title="留言（近 7 天）" size="small" className="card-interactive">
          <div className="flex flex-col gap-3 py-2">
            <div className="flex items-baseline gap-1.5">
              <span className="kpi-value num">
                {o?.comments ? formatNumber(o.comments.total) : '—'}
              </span>
              <span className="text-sm text-ink-500">条</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-ink-500">精选</span>
              <span className="text-primary-600 num font-medium">
                {o?.comments ? formatNumber(o.comments.elected) : '—'}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-ink-500">精选率</span>
              <span className="text-ink-700 num">
                {o?.comments && o.comments.total > 0
                  ? `${((o.comments.elected / o.comments.total) * 100).toFixed(1)}%`
                  : '—'}
              </span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}
